import "./customize-item-ui-base.css"

import EventEmitter from "eventemitter3";
import { DataLayerService } from "../../../../../services/data-layer-service";
import { Modal } from "../../../modal";
import { Data } from "../../../../../models/data";
import { Order } from "../../../../../models/order";
import { Context } from "../../../../../models/context";
import { ProfileService } from "../../../../../services/profile-service";
import { LocalizationService } from "../../../../../services/localization-service";
import { CustomizeItemCore } from "../../core/customize-item-core";
import { CustomizeItemUiOption, HalfModifierSelection, Modals, Pages } from "../../../../../types/enums";
import { Util } from "../../../../../utils/util";
import { IDataItem, IDataModifier, IDataModifierDisplayGroup, IItemModifierDisplayGroupModifier } from "../../../../../models/data.interface";
import { CustomizeItemSectionHeader } from "../shared/customize-item-section-header";
import { CustomizeItemSizeSection, ICustomizeItemSizeSection } from "../shared/customize-item-size-section";
import { ITabBarData } from "../../../../shared/TabBar/tab-bar.interface";
import { TabBar } from "../../../../shared/TabBar/tab-bar";
import { CustomizeItemFavorite } from "../shared/customize-item-favorite";
import { CustomizeItemSpecialInstructions } from "../shared/customize-item-special-instructions";
import { IOrderItemModifier } from "../../../../../models/order.interface";
import { EventTypes } from "../../../../../core/event-bus";
import { Router } from "../../../../../core/router";
import { DialogCreators } from "../../../../../utils/dialog-creators";
import { TimedMessage } from "../../../../shared/TimedMessage/timed-message";
import { IMergedModifier, ModifierRenderHalfState } from "../../core/customize-item-core.interface";
import { CustomizeItemModifierSingleItem } from "../single-item/customize-item-modifier";
import { CustomizeItemModifierMultiItem } from "../multi-item/customize-item-modifier";
import { ICustomizeItemModifierHalfBarClickEvent } from "../single-item/customize-item-half-bar";
import { ICustomizeItemModifierClickEvent, ICustomizeItemSizeChangeClickEvent } from "./customize-item-modifier-base.interface";
import { ICustomizeItemPreModifierClickEvent } from "./customize-item-premodifier-bar-base";

interface ICustomizeItemModules {
    core: CustomizeItemCore,
    sizeSection: CustomizeItemSizeSection | null,
    instructionsSection: CustomizeItemSpecialInstructions | null,
    favorite: CustomizeItemFavorite,
    modifiers: CustomizeItemModifierSingleItem[]
}

export interface ICustomizeItemUiParams {
    eventBus: EventEmitter,
    data: Data,
    context: Context,
    localizationService: LocalizationService,
    order: Order,
    dataLayerService: DataLayerService,
    profileService: ProfileService,
    core: CustomizeItemCore,
    favorite: CustomizeItemFavorite
}

export interface ICustomizeItemModulesParams {
    modalId: string,
    eventBus: EventEmitter,
    data: Data,
    context: Context,
    localizationService: LocalizationService,
    order: Order,
    dataLayerService: DataLayerService,
    profileService: ProfileService,
    modalName: Modals,
    core: CustomizeItemCore,
    favorite: CustomizeItemFavorite
}

export abstract class CustomizeItemUIBase extends Modal {
    protected _data: Data;
    protected _order: Order;
    protected _profileService: ProfileService;
    protected _modules: ICustomizeItemModules;

    protected _boundListeners: { event: string; handler: (...args: any[]) => void }[] = [];

    constructor(params: ICustomizeItemModulesParams) {
        super(
            params.modalId,
            params.eventBus,
            params.context,
            params.dataLayerService,
            params.localizationService,
            { hasCloseButton: true, ariaLabel: "Customize Item" },
            params.modalName);
        this._data = params.data;
        this._order = params.order;
        this._profileService = params.profileService;

        this._modules = this._createModules(params.core, params.favorite);
        this._scrollShadow();
    }

    /**
     * Opens the CustomizeItem class and sets up event listeners.
     */
    override open(): void {
        this._eventBusListeners();
        this._renderPage(true);
        this._setEventListeners();
        super.open();
    }

    /**
     * Closes the customization modal and removes associated event listeners.
     */
    override close(): void {
        super.close();
        Util.setElement("onclick", "btn_customize_item_quantity_subtract", null);
        Util.setElement("onclick", "btn_customize_item_quantity_add", null);
        Util.setElement("onclick", "btn_customize_item_add_to_order", null);
        Util.setElement("innerHTML", "div_customize_item_main_body", "");
        this._modules.favorite.clear();
        this.removeEventBusListeners();
    }

    protected _createModules(core: CustomizeItemCore, favorite: CustomizeItemFavorite): ICustomizeItemModules {
        return {
            core,
            favorite,
            sizeSection: null,
            instructionsSection: null,
            modifiers: []
        };
    }

    protected _setEventListeners(): void {
        Util.setElement("onclick", "btn_customize_item_quantity_subtract", this._handleSubtractQuantity);
        Util.setElement("onclick", "btn_customize_item_quantity_add", this._handleAddQuantity);
        Util.setElement("onclick", "btn_customize_item_add_to_order", this._handleAddToOrderAsync);
    }

    protected _trackListener(event: string, handler: (...args: any[]) => void): void {
        this._eventBus.on(event, handler);
        this._boundListeners.push({ event, handler });
    }

    public removeEventBusListeners(): void {
        this._boundListeners.forEach(({ event, handler }) => {
            this._eventBus.off(event, handler);
        });
        this._boundListeners = [];
    }

    protected _eventBusListeners(): void {
        this._trackListener(EventTypes.CUSTOMIZE_ITEM_PRICE_CHANGE, (price) => this._displayItemsPrice(price));
        this._trackListener(EventTypes.CUSTOMIZE_ITEM_CALORIES_CHANGE, this._updateItemCalories);
        this._trackListener(EventTypes.CUSTOMIZE_ITEM_MODIFIER_CLICKED, this._onModifierClicked);
        this._trackListener(EventTypes.CUSTOMIZE_ITEM_MODIFIER_SIZE_CLICKED, this._onItemSizeChangeClick);
        this._trackListener(EventTypes.CUSTOMIZE_ITEM_MODIFIER_HALF_CLICKED, this._onModifierHalfChangeClick);
        this._trackListener(EventTypes.CUSTOMIZE_ITEM_MODIFIER_PREMODIFIER_CLICKED, this._onModifierPreModifierChangeClick);
    }

    protected abstract _hideAndShowPreModifierMultiButtonsCheckbox(modifierDisplayGroupId: number | null, modifierId: number, halfId: HalfModifierSelection, isChecked: boolean): void;
    protected abstract _hideAndShowPreModifierButtonsRadio(modifierDisplayGroupId: number | null, modifierId: number, halfId: HalfModifierSelection, isChecked: boolean): void;

    /**
     * Renders the customization page for the item.
     * Updates UI elements and prepares item customization options.
     */
    protected _renderPage(isFirstRender: boolean = false): void {
        const menuItem = this._modules.core.getMenuItem(HalfModifierSelection.WHOLE);
        // Handle item image display
        if (menuItem.ImageURL) {
            Util.setElement("src", "img_customize_item_image", menuItem.ImageURL);
            Util.showElement("div_customize_item_image");
        } else {
            Util.hideElement("div_customize_item_image");
        }

        Util.showElement("div_customize_item_header_single");
        Util.hideElement("div_customize_item_header_multi");
        this._setAriaLabelledBy("h1_customize_item_name");

        //scroll to top of customize modal on init 
        if (this._modules.core.isHalfMode)
            Util.scrollToElementId("div_customize_item_main_body", 5, 30, 0, "smooth", {
                scrollContainerSelector: ".customize-item-content",
                stickyHeaderSelector: ".customize-item-header"
            });
        else
            Util.scrollToElementId("div_customize_item_image", 5, 30, 0, "smooth");

        this._modules.favorite.setFavoriteButtonRenderState();

        // Update item name and description
        const itemName = this._localizationService.GetName(menuItem.Names);
        Util.setElement("innerHTML", "h1_customize_item_name", itemName);
        Util.setElement("innerHTML", "h1_customize_item_multi_name", itemName);
        Util.setElement("alt", "img_customize_item_image", itemName);
        Util.setElement("innerHTML", "p_customize_item_description", this._localizationService.GetDescription(menuItem.Descriptions));

        // Clear and update main div content
        const mainDiv = document.getElementById("div_customize_item_main_body");
        if (!mainDiv)
            return;

        mainDiv.innerHTML = "";

        const mealDeal = this._modules.core.mealDeal;
        Util.setElement("disabled", "btn_customize_item_quantity_subtract", !!mealDeal)
        Util.setElement("disabled", "btn_customize_item_quantity_add", !!mealDeal)
        this._scrollToTop();

        // Update UI and functionality
        this._createItemSelections(isFirstRender);
        this._updateAllCalories();
        this._generateSpecialInstructionsSection();
        const buttonTxt = mealDeal ? this._localizationService.names("AddToDeal") : this._localizationService.names("AddToOrder");
        this._updateAddToOrderButtonText(buttonTxt);
        this._toggleItemCommentBoxVisibility();
        this._modules.core.recalculateItemPricing();

        this._scrollToTop();

        // Log view item if DataLayer is available
        if (this._dataLayer && isFirstRender) {
            const pageSource = Router.path === Pages.LANDING ? 'Homepage' : Router.path.replace('/', '');
            const itemListName = mealDeal ? Modals.MEAL_DEAL : pageSource;

            const firstItem = this._modules.core.firstItem;
            const orderItem = this._order._createOrderItem(
                menuItem,
                this._modules.core.sizeId,
                this._modules.core.isHalfHalf() ? 1 : 0,
                this._modules.core.quantity,
                firstItem.Modifiers,
                firstItem.BeforeTaxDiscount,
                firstItem.AfterTaxDiscount
            );
            this._dataLayer.view_item(this._order, orderItem, this._modules.core.elementIndex, itemListName);
        }

        this._context.setProperty("upsellSelected", false);
    }

    private readonly _onModifierHalfChangeClick = (params: ICustomizeItemModifierHalfBarClickEvent): void => {
        this._modules.core.applyHalfSelectionChange(params.modifierDisplayGroupId, params.modifierId, params.halfId);
        this._updateItemCalories(this._modules.core.firstItem.Modifiers, this._modules.core.secondItem.Modifiers);
    }

    private readonly _onModifierPreModifierChangeClick = (params: ICustomizeItemPreModifierClickEvent): void => {
        this._modules.core.updatePreModifier(params.modifierDisplayGroupId, params.modifierId, params.halfId, params.preModifierId);
        this._updateItemCalories(this._modules.core.firstItem.Modifiers, this._modules.core.secondItem.Modifiers);
    }

    /**
     * Reduces the item quantity by one when the "subtract" button is clicked.
     * Prevents reducing below 1.
     */
    protected readonly _handleSubtractQuantity = (): void => {
        if (this._modules.core.quantity > 1) {
            this._setQuantity(this._modules.core.quantity - 1);
            this._announce(`Quantity decreased to ${this._modules.core.quantity}`);
        } else {
            this._announce(`Minimum quantity is 1`);
        }
        this._displayItemsPrice(this._modules.core.firstItem.HalfHalfPrice);
    }


    /**
     * Increases the item quantity by one when the "add" button is clicked.
     */
    protected readonly _handleAddQuantity = (): void => {
        if (this._modules.core.quantity < 99) {
            this._setQuantity(this._modules.core.quantity + 1);
            this._announce(`Quantity increased to ${this._modules.core.quantity}`);
        } else {
            this._announce(`Maximum quantity is 99`);
        }
        this._displayItemsPrice(this._modules.core.firstItem.HalfHalfPrice);
    }

    protected readonly _onModifierClicked = (payload: ICustomizeItemModifierClickEvent): void => {
        const { elementId, displayGroupId, modifierId, halfId, isMultiSelect } = payload;

        const checkbox = document.getElementById(elementId) as HTMLInputElement | null;
        const modifierDisplayGroup = this._modules.core.getModifierDisplayGroupById(displayGroupId);

        if (!checkbox || !modifierDisplayGroup)
            return;

        if (this._modules.core.isSingleChoiceGroup(modifierDisplayGroup)
            && !modifierDisplayGroup.IsWeightChangeable
            && this._modules.core.getFirstModifierByModifierDisplayGroup(halfId, modifierDisplayGroup.ModifierDisplayGroupId)?.modifierId === modifierId)
            return;

        const result = this._modules.core.updateModifierSelection({
            modifierId,
            displayGroupId: modifierDisplayGroup.ModifierDisplayGroupId,
            halfId,
            isMultiSelect,
            isCheckboxCurrentlyChecked: checkbox.checked,
        });

        if (!result.success && result.forceRevertCheckbox)
            return;

        checkbox.checked = !checkbox.checked;
        this._hideAndShowPreModifierHalfable(
            isMultiSelect,
            this._modules.core.isHalfMode,
            modifierDisplayGroup,
            modifierId,
            halfId,
            checkbox.checked
        );

        if (this._dataLayer) {
            const modDisplayGroupNames = this._data.getProperty("ModifierDisplayGroups").find(x => x.ModifierDisplayGroupId === displayGroupId)?.Names ?? [];
            const modDisplayGroupName = this._localizationService.GetName(modDisplayGroupNames);

            const modifierIds = new Set(
                this._modules.core.modifiers
                    .filter(
                        x =>
                            x.displayGroupId === displayGroupId &&
                            x.isSelected &&
                            x.halfId === HalfModifierSelection.WHOLE
                    )
                    .map(y => y.modifier?.ModifierId)
            );

            const modifierNames = this._data
                .getProperty("Modifiers")
                .filter(x => modifierIds.has(x.ModifierId))
                .map(y => this._localizationService.GetName(y.Names));

            this._dataLayer.customize_option_selected(
                modDisplayGroupName,
                modifierNames.join(" | "),
                Router.path === Pages.LANDING ? "Homepage" : Router.path.replace("/", "")
            );
        }
    }

    private _hideAndShowPreModifierHalfable(
        isMultiSelect: boolean,
        isHalfMode: boolean,
        modifierDisplayGroup: IDataModifierDisplayGroup,
        modifierId: number,
        halfId: number,
        checked: boolean
    ): void {
        const isHalfableAndHalfMode = modifierDisplayGroup.IsHalfable && isHalfMode;
        const displayGroupId = modifierDisplayGroup.ModifierDisplayGroupId;

        if (isMultiSelect) {
            return isHalfableAndHalfMode
                ? this._hideAndShowPreModifierMultiButtonsCheckbox(displayGroupId, modifierId, halfId, checked)
                : this._hideAndShowPreModifierAndHalfableButtonsCheckbox(displayGroupId, modifierId);
        }

        return isHalfableAndHalfMode
            ? this._hideAndShowPreModifierButtonsRadio(displayGroupId, modifierId, halfId, checked)
            : this._hideAndShowPreModifierAndHalfableButtonsRadio(displayGroupId, modifierId, checked);
    }

    /**
     * Toggles visibility of pre-modifier and halfable buttons for radio inputs.
     * @param {number | null} modifierDisplayGroupId - The modifier display group ID.
     * @param {number} modifierId - The modifier ID.
     */
    private _hideAndShowPreModifierAndHalfableButtonsRadio(modifierDisplayGroupId: number | null, modifierId: number, isChecked: boolean): void {
        if (!modifierDisplayGroupId)
            return;

        const premodifierButtons = Array.from(document.querySelectorAll(`div[id^="div_customize_item_modifier_premodifier_selection_${modifierDisplayGroupId}_"]`)) as HTMLElement[];
        for (const button of premodifierButtons) {
            Util.hideElement(button);
        }

        const halfableButtons = Array.from(document.querySelectorAll(`div[id^="div_customize_item_modifier_halfable_selection_${modifierDisplayGroupId}_"]`)) as HTMLElement[];
        for (const button of halfableButtons) {
            Util.hideElement(button);
        }

        const checkboxes = Array.from(document.getElementsByName(`rdo_customize_item_modifier_display_group_${modifierDisplayGroupId}`)) as HTMLInputElement[];
        for (const checkbox of checkboxes) {
            checkbox.checked = false;
        }

        Util.setElement("checked", `rdo_customize_item_modifier_display_group_${modifierDisplayGroupId}_${modifierId}`, isChecked);

        if (isChecked) {
            Util.showElement(`div_customize_item_modifier_premodifier_selection_${modifierDisplayGroupId}_${modifierId}`);
            Util.showElement(`div_customize_item_modifier_halfable_selection_${modifierDisplayGroupId}_${modifierId}`);

            const premodEl = document.getElementById(`div_customize_item_modifier_premodifier_selection_${modifierDisplayGroupId}_${modifierId}`);
            if (premodEl) {
                premodEl.querySelectorAll<HTMLElement>('[role="option"]').forEach(opt => {
                    opt.tabIndex = -1;
                });
                const activeOpt = premodEl.querySelector<HTMLElement>('[role="option"].active');
                if (activeOpt) activeOpt.tabIndex = 0;
            }

            const premodContainer = document.getElementById(`div_customize_item_modifier_premodifier_selection_${modifierDisplayGroupId}_${modifierId}`);
            if (premodContainer && !premodContainer.querySelector('button.active')) {
                const defaultPreModId = this._modules.core.defaultPreModifierId;
                const defaultBtn = document.getElementById(`btn_customize_item_premodifier_${modifierDisplayGroupId}_${modifierId}_${defaultPreModId}`) as HTMLButtonElement | null;
                defaultBtn?.click();
            }
        }
    }

    /**
     * Handles the "Add to Order" button click event asynchronously.
     * Validates the item configuration, adds the selected items to the order,
     * and triggers additional processes such as meal deal handling and coupon application.
     */
    protected readonly _handleAddToOrderAsync = async (): Promise<void> => {
        const result = this._modules.core.createOrderItemsIfValid();

        if (!result.items) {
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                result.message ?? "Error_Generic",
                true
            );
            return;
        }

        const itemKey = await this._modules.core.finalizeOrderItemsAsync();

        // 2DO: Add Instruction Info
        //if (item.Instructions && document.getElementById("icon_customize_item_instructions_title")?.classList.contains('icon-show-less')) {
        //    const chkInstruction = Array.from(document.getElementsByName('chk_customize_item_Instructions')) as HTMLInputElement[];
        //    for (const instruction of chkInstruction) {
        //        if (instruction.checked)
        //            item.Instructions.push({ CommentId: parseInt(instruction.value) });
        //    }
        //}

        if (itemKey === -1)
            return;

        this.close();

        const shouldShowCartMessage = !this._modules.core.mealDeal && !this._modules.core.isEditing() || (this._modules.core.isEditing() && this._modules.core.recentOrder);
        if (shouldShowCartMessage) {
            const msgText = this._modules.core.quantity > 1
                ? this._localizationService.names("ItemsAddedToCart_Plural").replace("{{number}}", this._modules.core.quantity.toString())
                : this._localizationService.names("ItemsAddedToCart_Single");

            new TimedMessage(msgText, this._localizationService.names("ProceedToCartToCheckout"), 1000).show();
        }

        await this._modules.core.runPostOrderLogicAsync();

        await this._modules.favorite.favoriteItemProcessAsync(itemKey);

        if (this._dataLayer) {
            const addedItems = this._order.getProperty("Items").filter(x => x.ItemKey === itemKey);
            this._dataLayer.add_to_cart(this._order, addedItems, Router.path === Pages.LANDING ? 'Homepage' : Router.path.replace('/', ''), this._modules.core.mealDeal?.coupon?.CouponId);
        }
    }

    /**
     * Scrolls the customization content area to the top.
     */
    protected _scrollToTop(): void {
        const div = document.getElementById("div_customize_item_content");
        if (div)
            div.scrollTop = 0;
    }

    /**
     * Sets the item quantity and updates the corresponding UI element.
     * @param {number} quantity - The quantity to set.
     */
    protected _setQuantity(quantity: number): void {
        this._modules.core.quantity = quantity;
        Util.setElement("innerText", "spn_customize_item_quantity", quantity);
    }

    /**
     * Displays the item's price on the UI.
     * @param {number} halfHalfPrice - The price of the half-and-half item.
     */
    protected _displayItemsPrice(halfHalfPrice: number): void {
        const country = this._data.getProperty("StoreInfo").country;
        Util.setElement("innerText", "spn_customize_item_price", `${Util.formatMoney(halfHalfPrice * this._modules.core.quantity, country)}`);
    }

    /**
     * Updates the calorie and price information for all sizes and modifiers.
     */
    protected _updateAllCalories(): void {
        this._updateSizeCalories();
        this._updateModifierCalories();
    }

    /**
     * Updates the text of the "Add to Order" button.
     * @param {string} buttonText - The text to display on the button.
     */
    protected _updateAddToOrderButtonText(buttonText: string): void {
        Util.setElement("innerText", "spn_customize_item_add_to_order", buttonText);
    }

    /**
     * Toggles the visibility of the item comment box based on the OIC setting.
     */
    protected _toggleItemCommentBoxVisibility(): void {
        // 2DO: Needs to be designed for GUI and implemented

        //if (this._data.getProperty("Settings").OIC) //setting 75
        //    Util.hideElement("div_customize_item_comment");
        //else
        //    Util.showElement("div_customize_item_comment");
    }

    /**
     * Initializes item selections by setting the quantity, creating the GUI,
     * displaying item prices, and updating item calorie information.
     */
    protected _createItemSelections(firstRender: boolean): void {
        this._setQuantity(this._modules.core.quantity);
        this._createItemGui(firstRender);

        const firstItem = this._modules.core.firstItem;
        const secondItem = this._modules.core.secondItem;
        this._displayItemsPrice(firstItem.HalfHalfPrice);
        this._updateItemCalories(firstItem.Modifiers, secondItem.Modifiers);
    }

    /**
     * Creates the graphical user interface for customizing the item.
     * Dynamically adds sections like item sizes, single-choice modifiers,
     * and multi-choice modifiers to the UI.
     */
    protected _createItemGui(firstRender: boolean): void {
        // 2DO: Need to create comment box. Future US
        //if (oItem.Comment)
        //    Util.setElement("value", "txt_customize_item_comment", oItem.Comment);

        const mainDiv = document.getElementById("div_customize_item_main_body");
        if (!mainDiv)
            return;

        mainDiv.innerHTML = "";

        const menuItems = [this._modules.core.getMenuItem(HalfModifierSelection.LEFT), this._modules.core.getMenuItem(HalfModifierSelection.RIGHT)];
        const allowedSizes = this._modules.core.getValidItemSizes();

        // Setup item sizes
        this._createSizesSection(mainDiv, menuItems, allowedSizes);

        // 2DO: Some MealDeal Logic into the singleChoice Modifiers
        //    if (this._aOLO.Dialog.MealDeal) {
        //        const coupon = this._aOLO.Modules.Coupon.GetCoupon(this._aOLO.Dialog.MealDeal.GetCouponId(), true, this._order.getProperty("OrderTypeID"));
        //        if (coupon?.Items) {
        //            const selectedGroup = this._aOLO.Dialog.MealDeal.GetSelectedGroup();
        //            const allowedSizes = coupon.Items.filter(x => x.GroupIndex == selectedGroup).map(x => x.ItemId);
        //            items = items.filter(x => allowedSizes.includes(x.ItemId));
        //        }
        //    }

        const leftItem = menuItems[0];
        const rightItem = menuItems[1];

        // Add single-choice modifiers
        const singleChoiceModifiers = this._setSingleChoiceModifiers(leftItem, rightItem, firstRender);
        if (singleChoiceModifiers)
            mainDiv.appendChild(singleChoiceModifiers);

        // Add multi-choice modifiers
        const multiChoiceModifiers = this._setMultiChoiceModifiers(leftItem, rightItem, firstRender);
        if (multiChoiceModifiers)
            mainDiv.appendChild(multiChoiceModifiers);

        // Set default modifiers
        this._modules.core.applyDefaultModifiers();

        window.dispatchEvent(new Event("resize"));
    }

    /**
     * Generates and displays the "Special Instructions" section in the customization UI.
     */
    protected _generateSpecialInstructionsSection(): void {
        Util.hideElement("div_customize_item_special_instructions");
        Util.setElement("innerHTML", "div_customize_item_special_instructions", "");

        const parentDiv = document.getElementById("div_customize_item_special_instructions");
        if (!parentDiv)
            return;

        const menuItem = this._modules.core.getMenuItem(HalfModifierSelection.WHOLE);
        const instructionsSection = new CustomizeItemSpecialInstructions(
            this._context,
            this._localizationService,
            this._data.getProperty("Instructions"),
            menuItem.WebCategoryId,
            this._modules.core.sizeId,
            this._modules.core.instructions
        );
        this._modules.instructionsSection = instructionsSection;

        if (instructionsSection.element) {
            parentDiv.appendChild(instructionsSection.element);
            Util.showElement("div_customize_item_special_instructions");
        }
    }

    /**
     * Updates the UI to reflect the selected size.
     * @param {number} sizeId - The ID of the size to select.
     */
    protected _setSizeCheckbox(sizeId: number): void {
        // Reset all size labels
        const listbox = document.querySelector('[aria-label="Size"][role="listbox"]');
        listbox?.querySelectorAll<HTMLElement>('[role="option"]').forEach(opt => {
            opt.setAttribute("aria-selected", "false");
            opt.tabIndex = -1;
        });

        // Select the correct one
        const targetLabel = document.getElementById(`lbl_rdo_customize_item_size_${sizeId}`);
        if (targetLabel) {
            targetLabel.setAttribute("aria-selected", "true");
            targetLabel.tabIndex = 0;
        }

        Util.setElement("checked", `rdo_customize_item_size_${sizeId}`, true);
    }

    protected _createSizesSection(parentDiv: HTMLElement, menuItems: IDataItem[], allowedSizes: number[]): void {
        const params: ICustomizeItemSizeSection = {
            menuItems,
            orderTypeId: this._order.getProperty("OrderTypeID"),
            sizeId: this._modules.core.sizeId,
            allowedSizes: allowedSizes
        };

        const sizeSection = new CustomizeItemSizeSection(
            this._eventBus,
            this._data,
            this._context,
            this._localizationService,
            params
        );
        this._modules.sizeSection = sizeSection;

        if (sizeSection.element)
            parentDiv.appendChild(sizeSection.element);
    }

    /**
     * Creates a section for single-choice modifiers based on the item configuration.
     * @returns {HTMLElement | null} The HTML element containing single-choice modifiers, or null if none are applicable.
     */
    protected _setSingleChoiceModifiers(leftItem: IDataItem, rightItem: IDataItem, firstRender: boolean): HTMLElement | null {
        // Fast lookup: per-item displayGroup -> itemDisplayGroup
        const leftById = this._mapItemDisplayGroups(leftItem);
        const rightById = this._mapItemDisplayGroups(rightItem);

        const allDisplayGroups = this._data.getProperty("ModifierDisplayGroups");

        const unionIds = new Set<number>([
            ...leftById.keys(),
            ...rightById.keys()
        ]);

        // Map to actual displayGroup objects according to global order
        const displayGroupsToUse = allDisplayGroups.filter(g =>
            unionIds.has(g.ModifierDisplayGroupId)
        );

        if (displayGroupsToUse.length === 0)
            return null;

        const parentDiv = document.createElement("div");

        const commonNonHalfableById =
            this._modules.core.getCommonNonHalfableModifiers(leftItem, rightItem);

        for (const displayGroup of displayGroupsToUse) {
            const groupId = displayGroup.ModifierDisplayGroupId;

            // Grab the per-item group entries (might be undefined if an item doesn't contain the group)
            const leftItemGroup = leftById.get(groupId) ?? null;
            const rightItemGroup = rightById.get(groupId) ?? null;

            // Always update render state for invisible groups (keeps selection/validation logic consistent)
            if (!displayGroup.IsVisible) {
                this._primeInvisibleGroup(displayGroup, leftItemGroup, true);
                this._primeInvisibleGroup(displayGroup, rightItemGroup, false);
                continue;
            }

            // Honor your existing single-choice heuristic
            const isSingleChoice = this._modules.core.isSingleChoiceGroup(displayGroup);
            if (!isSingleChoice)
                continue;

            // Non-halfable: only render if it's common, use the group's own modifiers
            if (!displayGroup.IsHalfable) {
                const pairs = commonNonHalfableById.get(groupId);
                if (!pairs)
                    continue;

                const mergedModifiers: IMergedModifier[] = pairs.map(p => ({
                    leftModifier: p.left,
                    rightModifier: p.right
                }));

                const section = this._createSingleChoiceModifierSectionMerged(displayGroup, mergedModifiers, firstRender);
                parentDiv.appendChild(section);
                continue;
            }

            // Halfable: merge per-half options; disable per side when missing
            const mergedModifiers = this._getMergedSingleChoiceGroup(leftItemGroup, rightItemGroup);
            if (!mergedModifiers)
                continue;

            const section = this._createSingleChoiceModifierSectionMerged(displayGroup, mergedModifiers, firstRender);
            parentDiv.appendChild(section);
        }

        return parentDiv;
    }

    private _primeInvisibleGroup(
        displayGroup: IDataModifierDisplayGroup,
        itemGroup: IItemModifierDisplayGroupModifier[] | null,
        isLeft: boolean
    ): void {
        if (!itemGroup)
            return;

        const merged: IMergedModifier[] = itemGroup.map(m => ({
            leftModifier: isLeft ? m : null,
            rightModifier: isLeft ? null : m
        }));

        this._modules.core.getModifierRenderState(
            displayGroup,
            merged,
            null,
            false,
            false
        );
    }

    /**
     * Map an item's ItemDisplayGroups to a lookup by displayGroup id.
     * @param {IDataItem} item
     */
    private _mapItemDisplayGroups(item: IDataItem): Map<number, IItemModifierDisplayGroupModifier[]> {
        const map = new Map<number, IItemModifierDisplayGroupModifier[]>();
        for (const idg of item?.ModifierDisplayGroups ?? []) {
            map.set(idg.ModifierDisplayGroupId, idg.Modifiers ?? []);
        }
        return map;
    }

    /**
     * Merge left/right modifiers for a halfable, single-choice group.
     * If a modifier exists only on one side, it stays but is disabled on the missing side.
     * If neither side has the group, returns null.
     * @param {{Modifiers: IDataModifier[]}|null} leftItemGroup
     * @param {{Modifiers: IDataModifier[]}|null} rightItemGroup
     */
    private _getMergedSingleChoiceGroup(
        leftItemGroup: IItemModifierDisplayGroupModifier[] | null,
        rightItemGroup: IItemModifierDisplayGroupModifier[] | null
    ): IMergedModifier[] | null {
        const leftMods = leftItemGroup ?? [];
        const rightMods = rightItemGroup ?? [];

        if (leftMods.length === 0 && rightMods.length === 0)
            return null;

        // Index by ModifierId to unify the set
        const byId = new Map<number, { left?: IItemModifierDisplayGroupModifier; right?: IItemModifierDisplayGroupModifier }>();
        for (const m of leftMods) {
            const entry = byId.get(m.ModifierId) ?? {};
            entry.left = m;
            byId.set(m.ModifierId, entry);
        }

        for (const m of rightMods) {
            const entry = byId.get(m.ModifierId) ?? {};
            entry.right = m;
            byId.set(m.ModifierId, entry);
        }

        const mergedModifiers: IMergedModifier[] = [];
        for (const sides of byId.values()) {
            mergedModifiers.push({
                leftModifier: sides.left ?? null,
                rightModifier: sides.right ?? null
            });
        }

        mergedModifiers.sort((a, b) => {
            const aSort = (a.leftModifier ?? a.rightModifier)!.SortingIndex;
            const bSort = (b.leftModifier ?? b.rightModifier)!.SortingIndex;
            return aSort - bSort;
        });

        return mergedModifiers;
    }

    public getMergedSingleChoiceGroup(
        leftItem: IDataItem,
        rightItem: IDataItem,
        displayGroup: IDataModifierDisplayGroup
    ): { mergedModifiers: IMergedModifier[] } | null {

        const leftGroups = leftItem?.ModifierDisplayGroups ?? [];
        const rightGroups = rightItem?.ModifierDisplayGroups ?? [];

        // get (optional) group from each item
        const leftGroup = leftGroups.find(g => g.ModifierDisplayGroupId === displayGroup.ModifierDisplayGroupId);
        const rightGroup = rightGroups.find(g => g.ModifierDisplayGroupId === displayGroup.ModifierDisplayGroupId);

        const leftMods = leftGroup?.Modifiers ?? [];
        const rightMods = rightGroup?.Modifiers ?? [];

        const mergedModifiers = this._modules.core.mergeModifiers(leftMods, rightMods);
        if (!mergedModifiers.length)
            return null;

        return { mergedModifiers };
    }

    protected _setMultiChoiceModifiers(left: IDataItem, right: IDataItem, firstRender: boolean): HTMLElement | null {
        const mergedGroups = this._modules.core.getMergedModifierDisplayGroups(left, right);
        if (mergedGroups.length === 0)
            return null;

        const parentDiv = document.createElement("div");

        for (const group of mergedGroups) {
            const section = this._createMultiChoiceModifierSectionMerged(
                group.modifierDisplayGroup,
                group.modifiers,
                firstRender
            );
            parentDiv.appendChild(section);
        }

        return parentDiv;
    }

    protected _appendHeader(_parent: HTMLElement): void {
        // No implementation in base
    }

    /**
     * Updates the displayed calorie information for the current menu item.
     * Determines whether to calculate calories for a single (whole) item or both halves,
     * depending on whether the item is configured in half mode.
     *
     * @private
     * @param {IOrderItemModifier[]} modifiersLeft - List of modifiers applied to the left or whole item side.
     * @param {IOrderItemModifier[]} modifiersRight - List of modifiers applied to the right item side (used in half mode).
     * @returns {void}
     */
    private readonly _updateItemCalories = (modifiersLeft: IOrderItemModifier[], modifiersRight: IOrderItemModifier[]): void => {
        if (this._modules.core.isHalfMode) {
            this._updateItemCaloriesMulti(modifiersLeft,  HalfModifierSelection.LEFT);
            this._updateItemCaloriesMulti(modifiersRight, HalfModifierSelection.RIGHT);
        } else
            this._updateItemCaloriesSingle(modifiersLeft, modifiersRight);
    }

    /**
     * Updates the displayed calorie information for a full (non-half) menu item.
     *
     * @private
     * @param {IOrderItemModifier[]} modifiersLeft - List of modifiers applied to the primary item (whole or first half).
     * @param {IOrderItemModifier[]} modifiersRight - List of modifiers applied to the secondary half (if applicable).
     * @returns {void}
     */
    private _updateItemCaloriesSingle(modifiersLeft: IOrderItemModifier[], modifiersRight: IOrderItemModifier[]): void {
        const calories = this._modules.core.calculateItemCaloriesBySizeSingle(modifiersLeft, modifiersRight, this._order.getProperty("OrderTypeID"), this._modules.core.sizeId);
        Util.setElement("innerText", "p_customize_item_calories", this._localizationService.GetName(calories));
    }

    /**
     * Updates the displayed calorie information for a specific half of a menu item.
     *
     * @private
     * @param {IOrderItemModifier[]} modifiers - List of modifiers applied to the given half.
     * @param {HalfModifierSelection} halfId - Indicates which half (left or right) is being updated.
     * @returns {void}
     */
    private _updateItemCaloriesMulti(modifiers: IOrderItemModifier[], halfId: HalfModifierSelection): void {
        const calories = this._modules.core.calculateItemCaloriesBySizeMulti(modifiers, halfId, this._order.getProperty("OrderTypeID"), this._modules.core.sizeId);

        const id = halfId === HalfModifierSelection.RIGHT ? "second" : "first";
        Util.setElement("innerText", `spn_customize_item_header_multi_${id}_calories`, this._localizationService.GetName(calories));
    }

    /**
     * Toggles visibility of pre-modifier and halfable buttons for checkbox inputs.
     * @param {number | null} modifierDisplayGroupId - The modifier display group ID.
     * @param {number} modifierId - The modifier ID.
     */
    protected _hideAndShowPreModifierAndHalfableButtonsCheckbox(modifierDisplayGroupId: number | null, modifierId: number): void {
        if (!modifierDisplayGroupId)
            return;

        const checkboxId = `chk_customize_item_modifier_display_group_${modifierDisplayGroupId}_${modifierId}`;
        const isChecked = Util.getElementChecked(checkboxId);

        const action = isChecked ? Util.showElement : Util.hideElement;
        action(`div_customize_item_modifier_premodifier_selection_${modifierDisplayGroupId}_${modifierId}`);
        action(`div_customize_item_modifier_halfable_selection_${modifierDisplayGroupId}_${modifierId}`);
    }

    /**
     * Updates the calorie and price information for all available sizes.
     */
    private _updateSizeCalories(): void {
        if (!this._modules.sizeSection)
            return;

        for (const size of this._modules.sizeSection.sizes) {
            const sizeId = size.modifierId;
            const div = size.element.querySelector(`#spn_customize_item_modifier_size_price_cal_${sizeId}`);
            if (!div)
                continue;

            const { price, calories } = this._modules.core.calculatePriceAndCaloriesForSize(sizeId);
            div.innerHTML = this._getPriceAndCaloriesSpan(price, calories);
        }
    }

    /**
     * Updates the calorie and price information for all selected modifiers.
     */
    private _updateModifierCalories(): void {
        for (const modifier of this._modules.core.modifiers) {
            const div = document.querySelector(`#spn_customize_item_modifier_price_cal_${modifier.displayGroupId}_${modifier.modifierId}`);
            if (!div)
                continue;

            const { price, calories } = this._modules.core.getModifierPriceAndCalories(modifier.modifierId, modifier.displayGroupId);
            div.innerHTML = this._getPriceAndCaloriesSpan(price, calories);
        }
    }

    /**
     * Combines price and calorie information into a single formatted string.
     * @param {string} price - The formatted price string.
     * @param {string | null} calories - The calorie information or null.
     * @returns {string} A combined string of price and calories, or one if the other is missing.
     */
    private _getPriceAndCaloriesSpan(price: string, calories: string | null): string {
        if (price && calories)
            return `${price} | ${calories}`;

        return (price || calories) ?? "";
    }

    /**
     * Creates a section for a single-choice modifier group.
     * @param {IDataModifierDisplayGroup} modifierDisplayGroup - The data for the modifier display group.
     * @param {IMergedModifier[]} mergedModifiers - The list of modifiers in the group.
     * @returns {HTMLElement} The HTML element for the single-choice modifier section.
     */
    private _createSingleChoiceModifierSectionMerged(
        modifierDisplayGroup: IDataModifierDisplayGroup,
        mergedModifiers: IMergedModifier[],
        firstRender: boolean
    ): HTMLElement {
        this._sortMergedModifiers(mergedModifiers);

        const name = this._localizationService.GetName(modifierDisplayGroup.Names);
        const header = new CustomizeItemSectionHeader(this._localizationService, name).element;

        const modifierContent = this._getSelectedModifier(modifierDisplayGroup, mergedModifiers, firstRender);

        const element = document.createElement("div");
        element.classList.add("modifiers-selection-parent");
        element.appendChild(header);
        element.appendChild(modifierContent);

        return element;
    }

    private _createMultiChoiceModifierSectionMerged(
        modifierDisplayGroup: IDataModifierDisplayGroup,
        modifiers: IMergedModifier[],
        firstRender: boolean
    ): HTMLElement {
        const name = this._localizationService.GetName(modifierDisplayGroup.Names);

        const header = new CustomizeItemSectionHeader(this._localizationService, name, false, true).element;

        // Sort normally, using modifier.modifier.SortingIndex
        const sorted = modifiers
            .slice() // create a shallow copy
            .sort((a, b) => {
                const aBase = a.leftModifier ?? a.rightModifier!;
                const bBase = b.leftModifier ?? b.rightModifier!;

                const indexDiff = aBase.SortingIndex - bBase.SortingIndex;
                if (indexDiff !== 0)
                    return indexDiff;

                const aName = this._localizationService.GetName(JSON.parse(aBase.Name));
                const bName = this._localizationService.GetName(JSON.parse(bBase.Name));
                return aName.localeCompare(bName);
            });

        // Now group by web categories (same as before)
        const modifiersByWebCategory = this._modules.core.groupMergedModifiersByWebCategory(sorted);

        const categoriesSections = this._createMultiChoiceCategorySections(
            modifierDisplayGroup.ModifierDisplayGroupId,
            modifiersByWebCategory
        );

        this._populateMultiChoiceCategorySections(
            categoriesSections,
            modifierDisplayGroup,
            modifiersByWebCategory,
            this._data.getProperty("DefaultPreModifierId"),
            firstRender
        );

        const element = document.createElement("div");
        element.classList.add("modifiers-selection-parent");
        element.appendChild(header);
        element.appendChild(categoriesSections);

        return element;
    }

    /**
     * Creates tabbed sections for multi-choice modifier categories.
     * @param {number} modifierDisplayGroupId - The ID of the modifier display group.
     * @param {Record<number, IMergedModifier[]>} webCategories - The grouped modifiers by web category.
     * @returns {HTMLElement} - The container element for the category sections.
     */
    private _createMultiChoiceCategorySections(modifierDisplayGroupId: number, webCategories: Record<number, IMergedModifier[]>): HTMLElement {
        const mainDiv = document.createElement("div");

        const tabData: ITabBarData = {
            buttonId: `btn_customize_item_toppings_${modifierDisplayGroupId}`,
            tabs: []
        };

        const modifierCategories = this._data.getProperty("ModifierCategories");

        // Sort the web categories by the sorting index
        const sortedEntries = Object.entries(webCategories)
            .sort(([, a], [, b]) => {
                const aBase = a[0]?.leftModifier ?? a[0]?.rightModifier;
                const bBase = b[0]?.leftModifier ?? b[0]?.rightModifier;

                const aSort = aBase?.SortingIndex ?? 0;
                const bSort = bBase?.SortingIndex ?? 0;

                return aSort - bSort;
            });

        for (const [index, [webCategoryIdStr]] of sortedEntries.entries()) {
            const webCategoryId = Number(webCategoryIdStr);
            const modifierCategory = modifierCategories.find(x => x.ModifierWebCategoryId === webCategoryId);
            if (!modifierCategory)
                continue;

            const name = this._localizationService.GetName(modifierCategory.Names);
            tabData.tabs.push({
                id: modifierCategory.ModifierWebCategoryId,
                text: name,
                isSelected: index === 0,
                function: () => this._showItemsOfCategory(modifierDisplayGroupId, modifierCategory.ModifierWebCategoryId)
            });
        }

        const tabBar = new TabBar(tabData, this._localizationService);
        mainDiv.appendChild(tabBar.element);

        for (const [index, [webCategoryIdStr]] of sortedEntries.entries()) {
            const div = document.createElement("div");
            div.id = `div_customize_item_toppings_category_${modifierDisplayGroupId}_${webCategoryIdStr}`;
            div.setAttribute("name", `div_customize_item_toppings_category_${modifierDisplayGroupId}`);
            div.classList.add("customize-item-modifier-wrapper");

            if (index !== 0)
                Util.hideElement(div);

            mainDiv.appendChild(div);
        }

        return mainDiv;
    }

    /**
     * Populates the category sections for multi-choice modifiers with content.
     * @param {HTMLElement} parentDiv - The container element for category sections.
     * @param {IDataModifierDisplayGroup} modifierDisplayGroup - The modifier display group containing categories.
     * @param {Record<number, IMergedModifier[]>} webCategories - The grouped modifiers by web category.
     * @param {number} premodifierId - The ID of the premodifier to apply.
     */
    private _populateMultiChoiceCategorySections(
        parentDiv: HTMLElement,
        modifierDisplayGroup: IDataModifierDisplayGroup,
        webCategories: Record<number, IMergedModifier[]>,
        premodifierId: number,
        firstRender: boolean
    ) {
        for (const [webCategoryId, mergedModifiers] of Object.entries(webCategories)) {
            const categoryDiv = parentDiv.querySelector(`#div_customize_item_toppings_category_${modifierDisplayGroup.ModifierDisplayGroupId}_${webCategoryId}`);
            if (!categoryDiv)
                continue;

            this._populateMultiChoiceCategorySection(
                categoryDiv as HTMLElement,
                modifierDisplayGroup,
                mergedModifiers,
                premodifierId,
                firstRender
            );
        }
    }

    /**
     * Populates a specific category section with multi-choice modifiers.
     * @param {HTMLElement} categoryDiv - The container element for the category.
     * @param {IDataModifierDisplayGroup} modifierDisplayGroup - The modifier display group containing the category details.
     * @param {IMergedModifier[]} mergedModifiers - The list of modifiers to populate the category with.
     * @param {number} preModifierId - The ID of the pre-modifier.
     */
    private _populateMultiChoiceCategorySection(
        categoryDiv: HTMLElement,
        modifierDisplayGroup: IDataModifierDisplayGroup,
        mergedModifiers: IMergedModifier[],
        preModifierId: number,
        firstRender: boolean
    ) {
        this._sortMergedModifiers(mergedModifiers);

        const renderStates = this._modules.core.getModifierRenderState(
            modifierDisplayGroup,
            mergedModifiers,
            preModifierId,
            true,
            firstRender
        );

        const groupHasImage = renderStates.some(
            r => r.modifierData.ImageUrl && r.modifierData.ImageUrl.trim() !== ""
        );

        if (this._modules.core.isHalfMode)
            this._appendHeader(categoryDiv);

        for (const state of renderStates) {
            const option = this._createModifierOptionForMultiChoice(
                modifierDisplayGroup,
                state.leftModifier,
                state.rightModifier,
                state.modifierData,
                groupHasImage,
                state.selectedData
            );
            categoryDiv.appendChild(option);
        }
    }

    /**
     * Sort based on Modifiers->SortingIndex
     * @param mergedModifiers - The list of merged modifiers to sort.
     */
    private _sortMergedModifiers(mergedModifiers: IMergedModifier[]): void {
        mergedModifiers.sort((a, b) => {
            const aModifier = a.leftModifier ?? a.rightModifier!;
            const bModifier = b.leftModifier ?? b.rightModifier!;

            const aSortingIndex = this._data.getProperty("Modifiers").find(m => m.ModifierId === aModifier.ModifierId)?.SortingIndex ?? 0;
            const bSortingIndex = this._data.getProperty("Modifiers").find(m => m.ModifierId === bModifier.ModifierId)?.SortingIndex ?? 0;

            return aSortingIndex - bSortingIndex;
        });
    }

    /**
     * Creates an option for a multi-choice modifier.
     * @param {IDataModifierDisplayGroup} modifierDisplayGroup - The modifier display group data.
     * @param {IItemModifierDisplayGroupModifier} modifier - The modifier data.
     * @param {IDataModifier} modifierData - The additional modifier data.
     * @param {boolean} groupHasImage - Whether the group contains images.
     * @returns {HTMLElement} The HTML element for the modifier option.
     */
    private _createModifierOptionForMultiChoice(
        modifierDisplayGroup: IDataModifierDisplayGroup,
        leftModifier: IItemModifierDisplayGroupModifier | null,
        rightModifier: IItemModifierDisplayGroupModifier | null,
        modifierData: IDataModifier,
        groupHasImage: boolean,
        state: Map<HalfModifierSelection, ModifierRenderHalfState>
    ): HTMLElement {
        const modifierId = leftModifier?.ModifierId ?? rightModifier?.ModifierId!;
        const id = `chk_customize_item_modifier_display_group_${modifierDisplayGroup.ModifierDisplayGroupId}_${modifierId}`;
        const divName = `chk_customize_item_modifier_display_group_${modifierDisplayGroup.ModifierDisplayGroupId}`;
        const name = this._localizationService.GetName(modifierData.Names);

        const nonePreModifierId = this._data.getProperty("NonePreModifierId");
        
        return this._buildModifierElement(
            modifierDisplayGroup,
            leftModifier,
            rightModifier,
            modifierData,
            id,
            divName,
            name,
            groupHasImage,
            state,
            true,
            nonePreModifierId
        );
    }

    /**
     * Creates a section for a selected modifier in a single-choice group.
     * @param {IDataModifierDisplayGroup} modifierDisplayGroup - The modifier display group.
     * @param {IMergedModifier[]} mergedModifiers - The modifiers in the group.
     * @returns {HTMLElement} The container for the selected modifier options.
     */
    private _getSelectedModifier(
        modifierDisplayGroup: IDataModifierDisplayGroup,
        mergedModifiers: IMergedModifier[],
        firstRender: boolean
    ): HTMLElement {
        const modifierName = this._localizationService.GetName(modifierDisplayGroup.Names);
        const parentHtml = `
        <div role="listbox"
             aria-labelledby="rdo_customize_item_modifier_display_group_${modifierDisplayGroup.ModifierDisplayGroupId}_label"
             aria-label="${modifierName} Choose One"
             class="customize-item-modifier-wrapper">
        </div>
    `;

        const parentDiv = Util.createHtmlElementFromTemplate(parentHtml);

        const renderStates = this._modules.core.getModifierRenderState(
            modifierDisplayGroup,
            mergedModifiers,
            null,
            false,
            firstRender
        );

        const groupHasImage = renderStates.some(
            r => r.modifierData.ImageUrl && r.modifierData.ImageUrl.trim() !== ""
        );

        if (this._modules.core.isHalfMode && modifierDisplayGroup.IsHalfable)
            this._appendHeader(parentDiv);

        const nonePreModifierId = this._data.getProperty("NonePreModifierId");

        for (const state of renderStates) {
            const modifierId = state.leftModifier?.ModifierId ?? state.rightModifier?.ModifierId!;
            const name = this._localizationService.GetName(state.modifierData.Names);
            const id = `rdo_customize_item_modifier_display_group_${modifierDisplayGroup.ModifierDisplayGroupId}_${modifierId}`;
            const divName = `rdo_customize_item_modifier_display_group_${modifierDisplayGroup.ModifierDisplayGroupId}`;

            const element = this._buildModifierElement(
                modifierDisplayGroup,
                state.leftModifier,
                state.rightModifier,
                state.modifierData,
                id,
                divName,
                name,
                groupHasImage,
                state.selectedData,
                false,
                nonePreModifierId
            );

            parentDiv.appendChild(element);
        }

        return parentDiv;
    }

    private _buildModifierElement(
        modifierDisplayGroup: IDataModifierDisplayGroup,
        leftModifier: IItemModifierDisplayGroupModifier | null,
        rightModifier: IItemModifierDisplayGroupModifier | null,
        modifierData: IDataModifier,
        id: string,
        divName: string,
        name: string,
        groupHasImage: boolean,
        state: Map<HalfModifierSelection, ModifierRenderHalfState>,
        isMultiSelect: boolean,
        nonePreModifierId: number
    ): HTMLElement {
        const isHalfMode = this._modules.core.isHalfMode;
        const useSingleComponent =
            this._data.getProperty("Settings").OOS.customizeItemMode === CustomizeItemUiOption.SINGLE_ITEM
            || !isHalfMode
            || !modifierDisplayGroup.IsHalfable;

        const modifierId = leftModifier?.ModifierId ?? rightModifier?.ModifierId!;

        if (useSingleComponent) {
            const [key, value] = this._getSelectedHalf(state, nonePreModifierId);

            const element = new CustomizeItemModifierSingleItem(
                this._eventBus,
                this._data,
                this._context,
                this._localizationService,
                {
                    modifierDisplayGroup,
                    modifierId: modifierId,
                    leftModifier: leftModifier ?? rightModifier!,
                    rightModifier: null,
                    preModifierId: value?.preModifierId ?? nonePreModifierId,
                    halfId: key,
                    id,
                    name,
                    radioId: divName,
                    imageUrl: modifierData.ImageUrl,
                    menuItem: value.menuItem,
                    groupHasImage,
                    isSelected: value.isSelected,
                    isMultiSelect,
                    isHalfMode
                }
            );

            return element.element;
        }

        return new CustomizeItemModifierMultiItem(
            this._eventBus,
            this._data,
            this._context,
            this._localizationService,
            {
                modifierDisplayGroup,
                modifierId: modifierId,
                leftModifier,
                rightModifier,
                selectedData: state,
                id,
                name,
                radioId: divName,
                imageUrl: modifierData.ImageUrl,
                groupHasImage,
                isMultiSelect,
                isHalfMode
            }
        ).element;
    }

    private _getSelectedHalf(
        state: Map<HalfModifierSelection, ModifierRenderHalfState>,
        nonePreModifierId: number
    ): [HalfModifierSelection, ModifierRenderHalfState] {
        const pickState = (key: HalfModifierSelection) => {
            const value = state.get(key);
            return value === undefined ? undefined : [key, value] as const;
        };

        const entries = [...state.entries()];
        const found =
            entries.find(([_, v]) => v.preModifierId !== nonePreModifierId)
            ?? pickState(HalfModifierSelection.WHOLE)
            ?? pickState(HalfModifierSelection.LEFT);

        let [key, value] = found as [HalfModifierSelection, ModifierRenderHalfState];

        if (state.size > 1) {
            const values = [...state.values()];

            const samePreModifier = values.every(
                v => v.preModifierId === values[0].preModifierId
            );

            const sameSelected = values.every(
                v => v.isSelected === values[0].isSelected
            );

            if (!this._modules.core.isHalfMode) {
                const selectedWithModifier = [...state.entries()].filter(
                    ([_, v]) =>
                        v.isSelected &&
                        v.preModifierId !== nonePreModifierId
                );

                if (selectedWithModifier.length === 1) {
                    return selectedWithModifier[0];
                }
            }

            if (samePreModifier && sameSelected) {
                key = HalfModifierSelection.WHOLE;
                value = values[0];
            } else {
                key = HalfModifierSelection.WHOLE;
                value = {
                    ...value,
                    preModifierId: samePreModifier
                        ? values[0].preModifierId
                        : nonePreModifierId,
                    isSelected: values.every(v => v.isSelected)
                };
            }
        }

        return [key, value];
    }

    /**
     * Displays the items of a specific category while hiding others.
     * @param {number} modifierWebCategoryId - The ID of the modifier web category to display.
     */
    private readonly _showItemsOfCategory = (modifierDisplayGroupId: number, modifierWebCategoryId: number) => {
        const categoryDivs = Array.from(document.getElementsByName(`div_customize_item_toppings_category_${modifierDisplayGroupId}`));
        for (const div of categoryDivs) {
            Util.hideElement(div);
        }

        Util.showElement(`div_customize_item_toppings_category_${modifierDisplayGroupId}_${modifierWebCategoryId}`);
    }

    private readonly _onItemSizeChangeClick = ({ sizeId }: ICustomizeItemSizeChangeClickEvent): void => {
        const result = this._modules.core.validateSizeChange(sizeId);

        if (!result.valid) {
            this._setSizeCheckbox(result.sizeId);
            return;
        }

        this._itemSizeChangeContinue(sizeId);

        if (this._dataLayer) {
            const sizeNames = this._data.getProperty("Sizes").find(x => x.SizeId === sizeId)?.Names ?? [];
            const sizeName = this._localizationService.GetName(sizeNames);
            this._dataLayer.customize_option_selected("Size", sizeName, Router.path === Pages.LANDING ? "Homepage" : Router.path.replace("/", ""));
        }
    }

    /**
     * Continues the item size change process after validation.
     * Updates item properties, price, and calorie information.
     * @param {number} sizeId - The new size ID.
     */
    private _itemSizeChangeContinue(sizeId: number): void {
        const result = this._modules.core.applySizeChange(sizeId);

        if (!result) {
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                this._localizationService.names("Error_ReachedMaxNumberOfModifiers"),
                true
            );
            this._setSizeCheckbox(this._modules.core.sizeId);
            return;
        }

        this._setSizeCheckbox(sizeId);

        const menuItem = this._modules.core.getMenuItem(HalfModifierSelection.WHOLE);
        this._displayItemsPrice(this._modules.core.firstItem.HalfHalfPrice);
        this._updateAllCalories();
        this._checkHideHalfHalfBySize(menuItem);
        this._generateSpecialInstructionsSection();
        this._updateItemCalories(this._modules.core.firstItem.Modifiers, this._modules.core.secondItem.Modifiers);
    }

    /**
     * Checks whether the current size is halfable and updates the UI accordingly.
     */
    private _checkHideHalfHalfBySize(menuItem: IDataItem): void {
        const isHalfable = this._modules.core.isSizeHalfable(menuItem);

        // Tell core to reset state
        this._modules.core.normalizeHalfHalfState(isHalfable);

        // Update UI views accordingly
        for (const modifier of this._modules.modifiers) {
            if (isHalfable)
                this._showHalfBar(modifier);
            else
                modifier.clearHalfBar();
        }
    }

    /**
     * Displays the halfable toolbar for a specific modifier if applicable.
     * @param {ICustomizeItemModifier} modifier - The modifier to check.
     */
    private _showHalfBar(modifierClass: CustomizeItemModifierSingleItem): void {
        const modifierElement = this._modules.modifiers.find(x => x.modifierId === modifierClass.modifierId);
        if (!modifierElement)
            return;

        modifierElement.createHalfBar(false);

        const modifier = this._modules.core.modifiers.find(x => x.modifier?.ModifierId === modifierClass.modifierId);

        const parentDiv = document.getElementById(`div_customize_item_modifier_halfable_selection_${modifierClass.displayGroupId}_${modifierClass.modifierId}`);
        if (parentDiv && modifier?.isSelected)
            Util.showElement(parentDiv);
    }

    private _scrollShadow(): void {
        const scrollContainer = document.getElementById("div_customize_item_content");
        const pizzaDetails = scrollContainer?.querySelector("#div_customize_item_header") ?? null;
        const itemBody = scrollContainer?.querySelector("#div_customize_item_main_body") ?? null;

        if (scrollContainer && pizzaDetails && itemBody) {
            scrollContainer.addEventListener("scroll", () => {
                const topOffset = itemBody.getBoundingClientRect().top - pizzaDetails.getBoundingClientRect().bottom;
                
                if (topOffset < 0)
                    pizzaDetails.classList.add("scrolled");
                else
                    pizzaDetails.classList.remove("scrolled");
            });
        }

        const bottomBar = document.getElementById("div_customize_item_bottom");
        if (scrollContainer && bottomBar) {
            const toggleBottomBar = () => {
                const isScrollable = scrollContainer.scrollHeight > scrollContainer.clientHeight;
                const isAtBottom = scrollContainer.scrollTop + scrollContainer.clientHeight >= scrollContainer.scrollHeight;

                if (isScrollable && !isAtBottom) {
                    bottomBar.classList.add("scrolled");
                } else {
                    bottomBar.classList.remove("scrolled");
                }
            };

            // Initial check
            toggleBottomBar();

            // Listen for scroll events
            scrollContainer.addEventListener("scroll", toggleBottomBar);

            // Optional: Listen for resize or content changes if your modal content can dynamically grow
            window.addEventListener("resize", toggleBottomBar);
            const observer = new MutationObserver(toggleBottomBar);
            observer.observe(scrollContainer, { childList: true, subtree: true });
        }
    }
    private _announce(message: string): void {
        const announcer = document.getElementById("quantity-announcer");
        if (!announcer) return;

        // Reset text to force re-announcement if same content
        announcer.textContent = "";
        setTimeout(() => {
            announcer.textContent = message;
        }, 50);
    }
}